import React, { JSX, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useTranslations } from './LocalizationContext';

type HandScannerContextValue = {
  isConnected: boolean;
  isConnecting: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  setOnScan: (cb: ((data: string) => void) | null) => void;
};

const HandScannerContext = React.createContext<HandScannerContextValue | undefined>(undefined);

export function useHandScannerContext(): HandScannerContextValue {
  const ctx = useContext(HandScannerContext);
  if (!ctx) throw new Error('useHandScannerContext must be used within HandScannerContextProvider');
  return ctx;
}

export function HandScannerContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const t = useTranslations();
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [characteristic, setCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const bufferRef = useRef('');
  const onScanRef = useRef<((data: string) => void) | null>(null);

  const setOnScan = useCallback((cb: ((data: string) => void) | null) => {
    onScanRef.current = cb ?? null;
  }, []);

  const handleCharacteristicValueChanged = useCallback((event: Event) => {
    const target = event.target as unknown as BluetoothRemoteGATTCharacteristic;
    const value = new TextDecoder().decode(target.value as DataView);
    bufferRef.current += value;
    if (bufferRef.current.includes('\r')) {
      const fullMessage = bufferRef.current.trim();
      if (onScanRef.current) onScanRef.current(fullMessage);
      bufferRef.current = '';
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (characteristic) {
        try {
          await characteristic.stopNotifications();
          characteristic.removeEventListener('characteristicvaluechanged', handleCharacteristicValueChanged);
        } catch (e) {
          console.debug('Stopping notifications failed (ignored):', e);
        }
      }
      if (server?.connected) {
        try {
          server.disconnect();
        } catch (e) {
          console.debug('Server disconnect failed (ignored):', e);
        }
      }
    } finally {
      setDevice(null);
      setServer(null);
      setCharacteristic(null);
      bufferRef.current = '';
      setIsConnected(false);
    }
  }, [characteristic, handleCharacteristicValueChanged, server]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    try {
      if (!('bluetooth' in navigator)) {
        toast.error(t('hand_scanner_bt_not_supported'));
        return;
      }

      let bluetoothDevice: BluetoothDevice | undefined;
      try {
        bluetoothDevice = await navigator.bluetooth?.requestDevice({
          filters: [{ services: ['0000feea-0000-1000-8000-00805f9b34fb'] }],
        });
      } catch {
        // user cancelled chooser
        return;
      }

      if (!bluetoothDevice) return;
      setDevice(bluetoothDevice);
      if (!bluetoothDevice.gatt) throw new Error('Bluetooth device does not support GATT');

      const gattServer = await bluetoothDevice.gatt.connect();
      setServer(gattServer);

      const service = await gattServer.getPrimaryService('0000feea-0000-1000-8000-00805f9b34fb');
      const gattCharacteristic = await service.getCharacteristic('00002aa1-0000-1000-8000-00805f9b34fb');
      setCharacteristic(gattCharacteristic);

      await gattCharacteristic.startNotifications();
      gattCharacteristic.addEventListener('characteristicvaluechanged', handleCharacteristicValueChanged);
      setIsConnected(true);
    } catch (e) {
      console.error('Error connecting to Bluetooth device:', e);
      toast.error(t('hand_scanner_connect_error'));
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  }, [handleCharacteristicValueChanged, t]);

  // Listen for disconnects
  useEffect(() => {
    if (!device) return;
    const onGattServerDisconnected = () => {
      toast.warn(t('hand_scanner_disconnected'));
      void disconnect();
    };
    device.addEventListener('gattserverdisconnected', onGattServerDisconnected);
    return () => {
      device.removeEventListener('gattserverdisconnected', onGattServerDisconnected);
    };
  }, [device, disconnect, t]);

  const value: HandScannerContextValue = {
    isConnected,
    isConnecting,
    connect,
    disconnect,
    setOnScan,
  };

  return <HandScannerContext.Provider value={value}>{children}</HandScannerContext.Provider>;
}
